import { nativePageParams, nativePageRoutes } from '~/nativeLenses/pageRoutes';

import type { PersonalViewState } from '~/hooks/apis/reporting/usePersonalViews';

import { encodeLensView, lensViewIntegrity, lensViewParamValue, readLensView } from '../lensUrlState';

/** An empty exact view must take precedence over the next reader's personal default too. */
export const EXACT_VIEW_PARAM = 'viewScope';
export const VIEW_REPORT_VERSION_PARAM = 'viewVersion';
export type ViewKind = 'lens' | 'native';

const DIAGNOSTICS = ['fresh', 'debug', 'toolbarLayout', 'filterPlacement', EXACT_VIEW_PARAM, VIEW_REPORT_VERSION_PARAM];
const SENSITIVE_KEY =
  /^(?:token|access[-_]?token|id[-_]?token|refresh[-_]?token|auth|authorization|password|passwd|secret|client[-_]?secret|credential|credentials|session[-_]?id|cookie|email|email[-_]?address|signature|jwt|api[-_]?key|redirect|redirect[-_]?uri|return[-_]?url|callback)$/i;
const hasControl = (value: string) =>
  Array.from(value).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127);
const MAX_SEARCH_LENGTH = 12000;
const LEGACY_NATIVE_KEYS: Record<string, Record<string, string>> = {
  '/commercial-ops/commercial-kpi-tracker': { view: 'ck_view', metric: 'ck_metric', section: 'ck_section' },
  '/commercial-ops/contribution-margin': { view: 'cmview' },
  '/commercial-ops/performance-marketing': { view: 'pm_view', metric: 'pm_metric' },
  '/commercial-ops/flash-funnel': { view: 'flashView' },
  '/commercial-ops/customer-comms': { view: 'commsView' },
};

function checkedSearch(search: string): URLSearchParams {
  if (
    search.length > MAX_SEARCH_LENGTH ||
    (search && !search.startsWith('?')) ||
    hasControl(search) ||
    search.includes('#')
  ) {
    throw new Error('This view address is invalid or too long. Reset the view and try again.');
  }
  try {
    decodeURIComponent(search.replace(/\+/g, ' '));
  } catch {
    throw new Error('This view address is damaged. Reset the view and try again.');
  }
  const params = new URLSearchParams(search);
  if ([...params].length > 64 || [...params].some(([key, value]) => hasControl(key) || hasControl(value))) {
    throw new Error('This view address contains invalid parameters. Reset the view and try again.');
  }
  return params;
}

/** Save the existing URL contract, without resolving rolling dates into today's absolute dates. */
export function captureViewState(kind: ViewKind, search: string, pathname?: string): PersonalViewState {
  const params = checkedSearch(search);
  let scope: URLSearchParams;
  if (kind === 'lens') {
    if (params.getAll('lensView').length > 1)
      throw new Error('This link contains conflicting view state. Reset the filters before copying it.');
    const raw = lensViewParamValue(params);
    if (lensViewIntegrity(raw) === 'shortened') {
      throw new Error('This view link is damaged. Reset the filters before saving or copying it.');
    }
    scope = new URLSearchParams();
    const encoded = encodeLensView(readLensView(params));
    if (encoded) scope.set('lensView', encoded);
  } else {
    // These older app keys collide with the workspace. Never quietly strip a still-active choice.
    const legacy = pathname ? LEGACY_NATIVE_KEYS[pathname] : undefined;
    for (const key of ['view', 'metric', 'section']) {
      const value = params.get(key);
      if (
        value === null ||
        (key === 'view' && ['lens', 'lensIndex', 'thread', 'catalogue', 'certification'].includes(value))
      )
        continue;
      if (legacy?.[key] && params.has(legacy[key])) continue;
      throw new Error(
        'This older report address needs an update. Change a report filter or reset before saving or copying it.',
      );
    }
    scope = nativePageParams(search);
    DIAGNOSTICS.forEach((key) => scope.delete(key));
    for (const [key, value] of scope) {
      if (
        !/^[a-zA-Z][a-zA-Z0-9_.-]{0,79}$/.test(key) ||
        SENSITIVE_KEY.test(key) ||
        /(?:token|password|passwd|secret|credentials|apikey)$/.test(key.replace(/[^a-z0-9]/gi, '').toLowerCase()) ||
        ['__proto__', 'constructor', 'prototype', 'sessionkey', 'share', 'shareid'].includes(key.toLowerCase()) ||
        value.length > 4000 ||
        /@|(?:bearer\s|https?:\/\/|javascript:)|eyJ[a-zA-Z0-9_-]+\./i.test(value)
      ) {
        throw new Error('This address contains a parameter that cannot be saved or shared. Reset the view first.');
      }
    }
  }
  scope.sort();
  const query = scope.toString();
  if (query.length > MAX_SEARCH_LENGTH - 1) throw new Error('This view address is too long to save or copy.');
  return { version: 1, search: query ? `?${query}` : '' };
}

export function hasExplicitView(kind: ViewKind, search: string): boolean {
  const params = new URLSearchParams(search);
  if (params.has(EXACT_VIEW_PARAM)) return true;
  if (kind === 'lens') return lensViewParamValue(params) !== null;
  // A damaged address still belongs to its sender; never replace it with a personal default.
  try {
    return captureViewState(kind, search).search !== '';
  } catch {
    return true;
  }
}

/** State can supply only query values. The current compiled route supplies the destination. */
export function personalViewHref(
  kind: ViewKind,
  state: PersonalViewState,
  location: Pick<Location, 'pathname' | 'search'>,
): string {
  if (state?.version !== 1 || typeof state.search !== 'string') {
    throw new Error('This saved view uses an unsupported version. Save a new view from this report.');
  }
  const canonical = captureViewState(kind, state.search, location.pathname);
  // Revalidate stored state instead of silently dropping a field from an old or corrupt record.
  const given = checkedSearch(state.search);
  given.sort();
  if (given.toString() !== new URLSearchParams(canonical.search).toString()) {
    throw new Error('This saved view contains unsupported state. Open the published default and save a new view.');
  }
  const current = new URLSearchParams(location.search);
  const next = new URLSearchParams(canonical.search);
  if (kind === 'lens') {
    const legacy = /^\/le-lens\/[0-9a-f-]+\/?$/i.test(location.pathname);
    if (location.pathname !== '/ask-richard-unified' && !legacy)
      throw new Error('Open a Lens before restoring a view.');
    if (!legacy) {
      const id = current.get('lens');
      if (!id || !/^[0-9a-f-]{36}$/i.test(id)) throw new Error('The report address is missing its Lens identity.');
      next.set('view', 'lens');
      next.set('lens', id);
    }
  } else if (!nativePageRoutes.some((page) => location.pathname === page.path)) {
    throw new Error('Open the report overview before saving or restoring a view.');
  }
  next.set(EXACT_VIEW_PARAM, '1');
  return `${location.pathname}?${next}`;
}

export function exactViewHref(
  kind: ViewKind,
  location: Pick<Location, 'pathname' | 'search' | 'origin'>,
  reportVersion?: number,
): string {
  const href = new URL(
    personalViewHref(kind, captureViewState(kind, location.search, location.pathname), location),
    location.origin,
  );
  if (reportVersion && Number.isSafeInteger(reportVersion) && reportVersion > 0)
    href.searchParams.set(VIEW_REPORT_VERSION_PARAM, String(reportVersion));
  return href.href;
}
